import React, { Component, createElement, ErrorInfo } from 'react';
import { isDev } from '../../config';
import { DevError } from './dev-error';
import { ErrorBoundaryContext } from './error-boundary-context';

export interface FallbackProps {
  error: Error;
  reset: (...args: unknown[]) => void;
}

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  label?: string;
  fallback?: (error: FallbackProps) => React.ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: (
    details:
      | { reason: 'imperative-api'; args: unknown[] }
      | { reason: 'keys'; prev: unknown[] | undefined; next: unknown[] | undefined },
  ) => void;
  resetKeys?: string[];
}

interface CaughtError {
  didCatch: true;
  error: Error;
}

interface NoError {
  didCatch: false;
  error: null;
}

type ErrorBoundaryState = CaughtError | NoError;

const initialState: ErrorBoundaryState = {
  didCatch: false,
  error: null,
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
    this.state = initialState;
  }

  static getDerivedStateFromError(error: Error) {
    return { didCatch: true, error };
  }

  resetErrorBoundary(...args: unknown[]) {
    const { error } = this.state;

    if (error !== null) {
      this.props.onReset?.({
        args,
        reason: 'imperative-api',
      });

      this.setState(initialState);
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    const { didCatch } = this.state;
    const { resetKeys } = this.props;

    // There's an edge case where if the thing that triggered the error happens to *also* be in the resetKeys array,
    // we'd end up resetting the error boundary immediately.
    // This would likely trigger a second error to be thrown.
    // So we make sure that we don't check the resetKeys on the first call of cDU after the error is set.

    if (didCatch && prevState.error !== null && hasArrayChanged(prevProps.resetKeys, resetKeys)) {
      this.props.onReset?.({
        next: resetKeys,
        prev: prevProps.resetKeys,
        reason: 'keys',
      });

      this.setState(initialState);
    }
  }

  createElement(child: React.ReactNode) {
    const didCatch = this.state.didCatch;
    const error = this.state.error instanceof Error ? this.state.error : new Error(String(this.state.error));

    return createElement(
      ErrorBoundaryContext.Provider,
      {
        value: {
          didCatch,
          error,
          resetErrorBoundary: this.resetErrorBoundary,
        },
      },
      child,
    );
  }

  render() {
    const { children, fallback } = this.props;

    if (this.state.error === null) {
      return this.createElement(children);
    }

    const error = this.state.error instanceof Error ? this.state.error : new Error(String(this.state.error));

    if (fallback === undefined) {
      return isDev ? <DevError label={this.props.label} error={error} reset={this.resetErrorBoundary} /> : null;
    }

    const props: FallbackProps = {
      error,
      reset: this.resetErrorBoundary,
    };

    return this.createElement(fallback(props));
  }
}

function hasArrayChanged(a: unknown[] = [], b: unknown[] = []) {
  return a.length !== b.length || a.some((item, index) => !Object.is(item, b[index]));
}
